import { useEffect, useState, type ReactNode } from 'react';
import { Lock } from 'lucide-react';
import {
  AMENITY_CATALOG,
  AMENITY_GROUPS,
  ENERGY_CARRIER_LABELS,
  ENERGY_CERTIFICATE_TYPES,
  ENERGY_CLASSES,
  HEATING_SYSTEM_LABELS,
  KFW_STANDARD_LABELS,
  UNIT_SUBTYPES,
  UNIT_USAGES,
  amenityGroupLabels,
  energyCertificateTypeLabels,
  formatAmountInput,
  formatDate,
  parseAmountInput,
  parsePercentInput,
  unitExternalBlockLabels,
  unitSubtypeLabels,
  unitUsageLabels,
  type AmenityGroup,
  type Amenities,
  type EnergyCertificateType,
  type EnergyClass,
  type UnitSubtype,
  type UnitUsage,
} from '@brimo/shared';
import {
  Banner,
  Button,
  Checkbox,
  Dialog,
  Icon,
  Select,
  Tabs,
  TextField,
  TextLink,
  Toggle,
  type TabItem,
} from '../../../ds/components';
import { ApiError, errorMessage } from '../../../lib/api';
import { usePermissions } from '../../../lib/session';
import { updateUnit, usePropertyRecord } from '../api';
import { hasManagedFields, isManagedField, managedHint, withoutManagedFields } from '../availability';
import { formatNumber, parseDecimal, parseInteger } from '../format';
import { useOrganizationHints } from '../record/CalcDefaultsCard';
import type { PatchUnitBody, UnitDetail, UnitType } from '../types';
import { unitInheritanceHints, type UnitInheritanceHints } from './unitData';
import styles from '../record/dialogs.module.css';
import tabStyles from './UnitEditDialog.module.css';

/** The four tabs of „Einheit bearbeiten" (D-364). */
export type UnitEditTab = 'location' | 'rent' | 'calculation' | 'status';

export const UNIT_EDIT_TABS: ReadonlyArray<{ value: UnitEditTab; label: string }> = [
  { value: 'location', label: 'Lage & Ausstattung' },
  { value: 'rent', label: 'Miete & Kosten' },
  { value: 'calculation', label: 'Kalkulation' },
  { value: 'status', label: 'Status' },
];

export interface UnitEditDialogProps {
  open: boolean;
  unit: UnitDetail;
  /** The tab the dialog opens on (the card the user came from); default „Lage & Ausstattung". */
  initialTab?: UnitEditTab | undefined;
  onClose: () => void;
  onSaved: (unit: UnitDetail) => void;
}

type TextKey =
  | 'number'
  | 'building'
  | 'floor'
  | 'rooms'
  | 'areaSqm'
  | 'balconyAreaSqm'
  | 'orientation'
  | 'parkingSpaceNumber'
  | 'parkingSpacePriceCents'
  | 'cellar'
  | 'coOwnershipShare'
  | 'readyForOccupancy'
  | 'fittings'
  | 'rentedSinceOn'
  | 'priceCents'
  | 'commissionablePurchasePriceCents'
  | 'rentActualCents'
  | 'rentTargetCents'
  | 'rentSubsidyCents'
  | 'rentSubsidyCycles'
  | 'serviceChargeCents'
  | 'reserveShareCents'
  | 'sevCents'
  | 'wegCents'
  | 'otherNonRecoverableCents'
  | 'renovationSubsidyCents'
  | 'maintenanceExpenseCents'
  | 'parkingSpaceRentCents'
  | 'depreciationPct'
  | 'landSharePct'
  | 'oldBuildingSharePct'
  | 'street'
  | 'houseNumber'
  | 'floorPosition'
  | 'bathrooms'
  | 'landAreaSqm'
  | 'priceLandCents'
  | 'priceExistingBuildingCents'
  | 'renovationCents'
  | 'priceNewConstructionCents'
  | 'priceFurnitureCents'
  | 'priceOutdoorFacilitiesCents'
  | 'energyValueKwhSqm'
  | 'kfwStandard';

export type UnitDraft = Record<TextKey, string> & {
  type: UnitType;
  rented: boolean;
  subtype: UnitSubtype | '';
  usage: UnitUsage | '';
  salesReleased: boolean;
  sellerPaysAcquisitionCosts: boolean;
  energyCertificateType: EnergyCertificateType | '';
  energyClass: EnergyClass | '';
  energyCarriers: string[];
  heatingSystems: string[];
  amenities: Record<AmenityGroup, string[]>;
};

/** The tab of every field that can carry a validation error (badge count, D-364). */
const FIELD_TABS: Record<string, UnitEditTab> = {
  number: 'location',
  type: 'location',
  subtype: 'location',
  usage: 'location',
  building: 'location',
  floor: 'location',
  floorPosition: 'location',
  rooms: 'location',
  bathrooms: 'location',
  areaSqm: 'location',
  balconyAreaSqm: 'location',
  orientation: 'location',
  cellar: 'location',
  parkingSpaceNumber: 'location',
  parkingSpacePriceCents: 'location',
  fittings: 'location',
  amenities: 'location',
  energyCertificateType: 'location',
  energyValueKwhSqm: 'location',
  energyClass: 'location',
  energyCarriers: 'location',
  heatingSystems: 'location',
  kfwStandard: 'location',
  readyForOccupancy: 'location',
  street: 'location',
  houseNumber: 'location',
  landAreaSqm: 'location',
  rented: 'rent',
  rentedSinceOn: 'rent',
  rentActualCents: 'rent',
  rentTargetCents: 'rent',
  parkingSpaceRentCents: 'rent',
  serviceChargeCents: 'rent',
  reserveShareCents: 'rent',
  sevCents: 'rent',
  wegCents: 'rent',
  otherNonRecoverableCents: 'rent',
  rentSubsidyCents: 'rent',
  rentSubsidyCycles: 'rent',
  renovationSubsidyCents: 'rent',
  maintenanceExpenseCents: 'rent',
  priceCents: 'calculation',
  commissionablePurchasePriceCents: 'calculation',
  priceLandCents: 'calculation',
  priceExistingBuildingCents: 'calculation',
  renovationCents: 'calculation',
  priceNewConstructionCents: 'calculation',
  priceFurnitureCents: 'calculation',
  priceOutdoorFacilitiesCents: 'calculation',
  depreciationPct: 'calculation',
  landSharePct: 'calculation',
  oldBuildingSharePct: 'calculation',
  sellerPaysAcquisitionCosts: 'calculation',
  coOwnershipShare: 'calculation',
  salesReleased: 'status',
};

/** The tab of a field key; unknown keys (server errors on other fields) count on „Lage & Ausstattung". */
export function tabOfField(key: string): UnitEditTab {
  return FIELD_TABS[key] ?? 'location';
}

/** Errors per tab („Kalkulation · 2" as the tab's count badge). */
export function errorCountsByTab(errors: Record<string, string>): Record<UnitEditTab, number> {
  const counts: Record<UnitEditTab, number> = { location: 0, rent: 0, calculation: 0, status: 0 };
  for (const key of Object.keys(errors)) counts[tabOfField(key)] += 1;
  return counts;
}

const money = (cents: number | null): string => (cents === null ? '' : formatAmountInput(cents));
const num = (value: number | null, decimals = 1): string =>
  value === null ? '' : formatNumber(value, decimals);
/** Native number inputs take the dot notation („2.05"); `parsePercentInput` reads both. */
const pct = (value: number | null): string => (value === null ? '' : String(value));

const AMOUNT_MESSAGE = 'Bitte einen Betrag ab 0 € angeben.';
const CYCLES_MESSAGE = 'Bitte eine ganze Zahl ab 0 angeben.';
const DEPRECIATION_MESSAGE = 'Bitte einen Wert zwischen 0 und 10 % angeben.';
const SHARE_MESSAGE = 'Bitte einen Wert zwischen 0 und 100 % angeben.';

export function unitDraftOf(unit: UnitDetail): UnitDraft {
  const f = unit.fields;
  return {
    number: unit.number,
    type: unit.type,
    building: f.building ?? '',
    floor: f.floor ?? '',
    rooms: num(f.rooms, 1),
    areaSqm: num(f.areaSqm, 1),
    balconyAreaSqm: num(f.balconyAreaSqm, 1),
    orientation: f.orientation ?? '',
    parkingSpaceNumber: f.parkingSpaceNumber ?? '',
    parkingSpacePriceCents: money(f.parkingSpacePriceCents),
    cellar: f.cellar ?? '',
    coOwnershipShare: num(f.coOwnershipShare, 2),
    readyForOccupancy: f.readyForOccupancy ?? '',
    fittings: f.fittings ?? '',
    rented: f.rented,
    rentedSinceOn: f.rentedSinceOn ?? '',
    priceCents: money(f.priceCents),
    commissionablePurchasePriceCents: money(f.commissionablePurchasePriceCents),
    rentActualCents: money(f.rentActualCents),
    rentTargetCents: money(f.rentTargetCents),
    rentSubsidyCents: money(f.rentSubsidyCents),
    rentSubsidyCycles: f.rentSubsidyCycles === null ? '' : String(f.rentSubsidyCycles),
    serviceChargeCents: money(f.serviceChargeCents),
    reserveShareCents: money(f.reserveShareCents),
    sevCents: money(f.sevCents),
    wegCents: money(f.wegCents),
    otherNonRecoverableCents: money(f.otherNonRecoverableCents),
    sellerPaysAcquisitionCosts: f.sellerPaysAcquisitionCosts,
    renovationSubsidyCents: money(f.renovationSubsidyCents),
    maintenanceExpenseCents: money(f.maintenanceExpenseCents),
    parkingSpaceRentCents: money(f.parkingSpaceRentCents),
    depreciationPct: pct(f.depreciationPct),
    landSharePct: pct(f.landSharePct),
    oldBuildingSharePct: pct(f.oldBuildingSharePct),
    subtype: f.subtype ?? '',
    usage: f.usage ?? '',
    street: f.street ?? '',
    houseNumber: f.houseNumber ?? '',
    floorPosition: f.floorPosition ?? '',
    bathrooms: num(f.bathrooms, 0),
    landAreaSqm: num(f.landAreaSqm, 1),
    salesReleased: f.salesReleased,
    priceLandCents: money(f.priceLandCents),
    priceExistingBuildingCents: money(f.priceExistingBuildingCents),
    renovationCents: money(f.renovationCents),
    priceNewConstructionCents: money(f.priceNewConstructionCents),
    priceFurnitureCents: money(f.priceFurnitureCents),
    priceOutdoorFacilitiesCents: money(f.priceOutdoorFacilitiesCents),
    energyCertificateType: f.energyCertificateType ?? '',
    energyValueKwhSqm: num(f.energyValueKwhSqm, 2),
    energyClass: f.energyClass ?? '',
    energyCarriers: [...f.energyCarriers],
    heatingSystems: [...f.heatingSystems],
    kfwStandard: f.kfwStandard ?? '',
    amenities: {
      rooms: [...(f.amenities.rooms ?? [])],
      kitchen: [...(f.amenities.kitchen ?? [])],
      bathroom: [...(f.amenities.bathroom ?? [])],
      floor: [...(f.amenities.floor ?? [])],
      other: [...(f.amenities.other ?? [])],
    },
  };
}

const text = (value: string): string | null => (value.trim() === '' ? null : value.trim());

function toggled(list: readonly string[], key: string, on: boolean): string[] {
  return on ? [...list.filter((entry) => entry !== key), key] : list.filter((entry) => entry !== key);
}

/** Only the groups with a selection (the API stores `{}` for „nothing selected"). */
function amenitiesBody(amenities: UnitDraft['amenities']): Amenities {
  const body: Record<string, string[]> = {};
  for (const group of AMENITY_GROUPS) {
    const selected = Object.keys(AMENITY_CATALOG[group]).filter((key) => amenities[group].includes(key));
    if (selected.length > 0) body[group] = selected;
  }
  return body;
}

/**
 * The draft → the PATCH body plus the client-side field errors (the body is only sent without errors). Locked
 * TeamProQ fields are never validated (they are stripped by `withoutManagedFields` before sending).
 */
export function unitPatchOf(
  draft: UnitDraft,
  locked: (field: string) => boolean,
): { body: PatchUnitBody; errors: Record<string, string> } {
  const errors: Record<string, string> = {};
  if (!draft.number.trim()) errors['number'] = 'Bitte eine Bezeichnung angeben.';
  const numeric = (value: string, key: string, parse: (t: string) => number | null): number | null => {
    if (value.trim() === '') return null;
    const parsed = parse(value);
    if (parsed === null && !locked(key)) errors[key] = 'Bitte eine Zahl angeben.';
    return parsed;
  };
  /** Calculator amounts (owner decision 19.10.2026): readable and not negative. */
  const amount = (key: TextKey): number | null => {
    const parsed = numeric(draft[key], key, parseAmountInput);
    if (parsed !== null && parsed < 0 && !locked(key)) errors[key] = AMOUNT_MESSAGE;
    return parsed;
  };
  /** A percentage between 0 and `max` (three decimals at most). */
  const percent = (key: TextKey, max: number, message: string): number | null => {
    if (draft[key].trim() === '') return null;
    const parsed = parsePercentInput(draft[key]);
    if ((parsed === null || parsed < 0 || parsed > max) && !locked(key)) errors[key] = message;
    return parsed;
  };
  const cycles = ((): number | null => {
    const value = draft.rentSubsidyCycles.trim();
    if (value === '') return null;
    if (!/^\d+$/.test(value)) {
      errors['rentSubsidyCycles'] = CYCLES_MESSAGE;
      return null;
    }
    return Number(value);
  })();
  const body: PatchUnitBody = {
    number: draft.number.trim(),
    type: draft.type,
    building: text(draft.building),
    floor: text(draft.floor),
    rooms: numeric(draft.rooms, 'rooms', parseDecimal),
    areaSqm: numeric(draft.areaSqm, 'areaSqm', parseDecimal),
    balconyAreaSqm: numeric(draft.balconyAreaSqm, 'balconyAreaSqm', parseDecimal),
    orientation: text(draft.orientation),
    parkingSpaceNumber: text(draft.parkingSpaceNumber),
    parkingSpacePriceCents: numeric(draft.parkingSpacePriceCents, 'parkingSpacePriceCents', parseAmountInput),
    cellar: text(draft.cellar),
    coOwnershipShare: numeric(draft.coOwnershipShare, 'coOwnershipShare', parseDecimal),
    readyForOccupancy: text(draft.readyForOccupancy),
    fittings: text(draft.fittings),
    rented: draft.rented,
    rentedSinceOn: draft.rented ? text(draft.rentedSinceOn) : null,
    priceCents: numeric(draft.priceCents, 'priceCents', parseAmountInput),
    commissionablePurchasePriceCents: numeric(
      draft.commissionablePurchasePriceCents,
      'commissionablePurchasePriceCents',
      parseAmountInput,
    ),
    rentActualCents: numeric(draft.rentActualCents, 'rentActualCents', parseAmountInput),
    rentTargetCents: numeric(draft.rentTargetCents, 'rentTargetCents', parseAmountInput),
    rentSubsidyCents: numeric(draft.rentSubsidyCents, 'rentSubsidyCents', parseAmountInput),
    rentSubsidyCycles: cycles,
    serviceChargeCents: numeric(draft.serviceChargeCents, 'serviceChargeCents', parseAmountInput),
    reserveShareCents: numeric(draft.reserveShareCents, 'reserveShareCents', parseAmountInput),
    sevCents: amount('sevCents'),
    wegCents: amount('wegCents'),
    otherNonRecoverableCents: amount('otherNonRecoverableCents'),
    sellerPaysAcquisitionCosts: draft.sellerPaysAcquisitionCosts,
    renovationSubsidyCents: amount('renovationSubsidyCents'),
    maintenanceExpenseCents: amount('maintenanceExpenseCents'),
    parkingSpaceRentCents: amount('parkingSpaceRentCents'),
    depreciationPct: percent('depreciationPct', 10, DEPRECIATION_MESSAGE),
    landSharePct: percent('landSharePct', 100, SHARE_MESSAGE),
    oldBuildingSharePct: percent('oldBuildingSharePct', 100, SHARE_MESSAGE),
    subtype: draft.subtype === '' ? null : draft.subtype,
    usage: draft.usage === '' ? null : draft.usage,
    street: text(draft.street),
    houseNumber: text(draft.houseNumber),
    floorPosition: text(draft.floorPosition),
    bathrooms: numeric(draft.bathrooms, 'bathrooms', parseInteger),
    landAreaSqm: numeric(draft.landAreaSqm, 'landAreaSqm', parseDecimal),
    salesReleased: draft.salesReleased,
    priceLandCents: numeric(draft.priceLandCents, 'priceLandCents', parseAmountInput),
    priceExistingBuildingCents: numeric(
      draft.priceExistingBuildingCents,
      'priceExistingBuildingCents',
      parseAmountInput,
    ),
    renovationCents: numeric(draft.renovationCents, 'renovationCents', parseAmountInput),
    priceNewConstructionCents: numeric(
      draft.priceNewConstructionCents,
      'priceNewConstructionCents',
      parseAmountInput,
    ),
    priceFurnitureCents: numeric(draft.priceFurnitureCents, 'priceFurnitureCents', parseAmountInput),
    priceOutdoorFacilitiesCents: numeric(
      draft.priceOutdoorFacilitiesCents,
      'priceOutdoorFacilitiesCents',
      parseAmountInput,
    ),
    amenities: amenitiesBody(draft.amenities),
    energyCertificateType: draft.energyCertificateType === '' ? null : draft.energyCertificateType,
    energyValueKwhSqm: numeric(draft.energyValueKwhSqm, 'energyValueKwhSqm', parseDecimal),
    energyClass: draft.energyClass === '' ? null : draft.energyClass,
    energyCarriers: draft.energyCarriers,
    heatingSystems: draft.heatingSystems,
    kfwStandard: text(draft.kfwStandard),
  };
  return { body, errors };
}

/** Where an inherited value is changed (D-364/D-363): the links under the inherited fields. */
export interface InheritanceLinks {
  /** `/properties/:id?calc=1` (sheet „Objekt-Vorgaben"); null without edit right on the property. */
  property: string | null;
  /** The organization's assumptions; null without `settings.organization`. */
  organization: string | null;
}

export interface UnitEditFormProps {
  unit: UnitDetail;
  draft: UnitDraft;
  fields: Record<string, string>;
  tab: UnitEditTab;
  onTab: (tab: UnitEditTab) => void;
  onChange: <K extends keyof UnitDraft>(key: K, value: UnitDraft[K]) => void;
  hints: UnitInheritanceHints;
  links: InheritanceLinks;
}

/**
 * Objekte 04 „Einheit bearbeiten" in four DS tabs (Nico Kremer's feedback 01.11.2026, D-364): Lage & Ausstattung,
 * Miete & Kosten, Kalkulation, Status. Every field of the former flat form is on exactly one tab; a tab with
 * validation errors carries their count. Inherited fields (AfA, SE-/WEG-Verwaltung, Rücklage, sonstige Kosten) show
 * OBJCALC's hint while empty and the links to the place where the inherited value is changed.
 */
export function UnitEditForm({ unit, draft, fields, tab, onTab, onChange, hints, links }: UnitEditFormProps) {
  const external = unit.external;
  const locked = (field: string): boolean => isManagedField(external, field);
  const lockIcon: ReactNode = <Icon icon={Lock} size={16} aria-label="Gesperrt" />;
  /** Props of a text field / select that TeamProQ owns. */
  const lock = (field: string, hint?: string) =>
    locked(field) && external
      ? { readOnly: true, hint: managedHint(external), labelEnd: lockIcon }
      : { hint };
  /** The inheritance hint of an empty inherited field (D-363). */
  const inherited = (key: keyof UnitInheritanceHints): string | undefined =>
    draft[key].trim() === '' ? hints[key] : undefined;
  const counts = errorCountsByTab(fields);
  const items: TabItem<UnitEditTab>[] = UNIT_EDIT_TABS.map((item) => ({
    value: item.value,
    label: item.label,
    count: counts[item.value] > 0 ? counts[item.value] : undefined,
  }));

  const moneyField = (key: TextKey, label: string, hint?: string) => (
    <TextField
      label={label}
      type="money"
      value={draft[key]}
      error={fields[key]}
      {...lock(key, hint)}
      onChange={(event) => onChange(key, event.target.value)}
    />
  );

  const percentField = (key: TextKey, label: string, step: number, max: number, hint?: string) => (
    <TextField
      label={label}
      type="number"
      inputMode="decimal"
      min={0}
      max={max}
      step={step}
      trailing={<span className={styles.suffix}>%</span>}
      value={draft[key]}
      error={fields[key]}
      {...lock(key, hint)}
      onChange={(event) => onChange(key, event.target.value)}
    />
  );

  const inheritanceLinks =
    links.property || links.organization ? (
      <p className={styles.helper}>
        Leere Felder übernehmen die Objekt-Vorgaben, sonst die Vorgaben der Organisation.{' '}
        {links.property ? (
          <TextLink to={links.property} size="small">
            Objekt-Vorgaben ändern
          </TextLink>
        ) : null}
        {links.property && links.organization ? ' · ' : null}
        {links.organization ? (
          <TextLink to={links.organization} size="small">
            Vorgaben der Organisation ändern
          </TextLink>
        ) : null}
      </p>
    ) : null;

  const location = (
    <>
      <div className={styles.group}>
        <div className={styles.groupTitle}>Lage</div>
        <div className={styles.grid2}>
          <TextField
            label="Bezeichnung"
            required
            value={draft.number}
            error={fields['number']}
            {...lock('number')}
            onChange={(event) => onChange('number', event.target.value)}
          />
          <Select
            label="Typ"
            value={draft.type}
            options={[
              { value: 'apartment', label: 'Wohnung' },
              { value: 'parking_space', label: 'Stellplatz' },
              { value: 'commercial', label: 'Gewerbe' },
              { value: 'other', label: 'Sonstiges' },
            ]}
            {...lock('type')}
            onChange={(event) => onChange('type', event.target.value as UnitType)}
          />
          <Select
            label="Untertyp"
            value={draft.subtype}
            placeholder="Keine Angabe"
            options={UNIT_SUBTYPES.map((key) => ({ value: key, label: unitSubtypeLabels[key] }))}
            {...lock('subtype')}
            onChange={(event) => onChange('subtype', event.target.value as UnitSubtype | '')}
          />
          <Select
            label="Nutzung"
            value={draft.usage}
            placeholder="Keine Angabe"
            options={UNIT_USAGES.map((key) => ({ value: key, label: unitUsageLabels[key] }))}
            {...lock('usage')}
            onChange={(event) => onChange('usage', event.target.value as UnitUsage | '')}
          />
          <TextField
            label="Haus"
            value={draft.building}
            onChange={(event) => onChange('building', event.target.value)}
          />
          <TextField
            label="Etage"
            value={draft.floor}
            {...lock('floor')}
            onChange={(event) => onChange('floor', event.target.value)}
          />
          <TextField
            label="Lage im Geschoss"
            value={draft.floorPosition}
            {...lock('floorPosition', 'z. B. „links“, „Mitte“, „rechts“')}
            onChange={(event) => onChange('floorPosition', event.target.value)}
          />
          <TextField
            label="Zimmer"
            inputMode="decimal"
            value={draft.rooms}
            error={fields['rooms']}
            {...lock('rooms')}
            onChange={(event) => onChange('rooms', event.target.value)}
          />
          <TextField
            label="Bäder"
            inputMode="numeric"
            value={draft.bathrooms}
            error={fields['bathrooms']}
            {...lock('bathrooms')}
            onChange={(event) => onChange('bathrooms', event.target.value)}
          />
          <TextField
            label="Wohnfläche"
            inputMode="decimal"
            trailing={<span className={styles.suffix}>m²</span>}
            value={draft.areaSqm}
            error={fields['areaSqm']}
            {...lock('areaSqm')}
            onChange={(event) => onChange('areaSqm', event.target.value)}
          />
          <TextField
            label="Balkon / Terrasse"
            inputMode="decimal"
            trailing={<span className={styles.suffix}>m²</span>}
            value={draft.balconyAreaSqm}
            error={fields['balconyAreaSqm']}
            onChange={(event) => onChange('balconyAreaSqm', event.target.value)}
          />
          <TextField
            label="Ausrichtung"
            value={draft.orientation}
            onChange={(event) => onChange('orientation', event.target.value)}
          />
          <TextField
            label="Keller"
            value={draft.cellar}
            onChange={(event) => onChange('cellar', event.target.value)}
          />
          <TextField
            label="Stellplatz"
            value={draft.parkingSpaceNumber}
            hint="z. B. „TG 14“"
            onChange={(event) => onChange('parkingSpaceNumber', event.target.value)}
          />
          <TextField
            label="Stellplatzpreis"
            type="money"
            value={draft.parkingSpacePriceCents}
            error={fields['parkingSpacePriceCents']}
            onChange={(event) => onChange('parkingSpacePriceCents', event.target.value)}
          />
          <TextField
            label="Bezugsfertig"
            value={draft.readyForOccupancy}
            onChange={(event) => onChange('readyForOccupancy', event.target.value)}
          />
          <TextField
            label="Ausstattung"
            value={draft.fittings}
            onChange={(event) => onChange('fittings', event.target.value)}
          />
          <TextField
            label="Grundstücksfläche"
            inputMode="decimal"
            trailing={<span className={styles.suffix}>m²</span>}
            value={draft.landAreaSqm}
            error={fields['landAreaSqm']}
            {...lock('landAreaSqm')}
            onChange={(event) => onChange('landAreaSqm', event.target.value)}
          />
          <TextField
            label="Straße"
            value={draft.street}
            {...lock('street', 'Nur wenn abweichend von der Objektadresse')}
            onChange={(event) => onChange('street', event.target.value)}
          />
          <TextField
            label="Hausnummer"
            value={draft.houseNumber}
            {...lock('houseNumber')}
            onChange={(event) => onChange('houseNumber', event.target.value)}
          />
        </div>
      </div>
      <div className={styles.group}>
        <div className={styles.groupTitle}>Ausstattungsmerkmale</div>
        {locked('amenities') && external ? (
          <span className={styles.lockNote}>{managedHint(external)}</span>
        ) : null}
        {AMENITY_GROUPS.map((group) => (
          <fieldset key={group} className={styles.checkGroup}>
            <legend className={styles.fieldLabel}>{amenityGroupLabels[group]}</legend>
            <div className={styles.checkGrid}>
              {Object.entries(AMENITY_CATALOG[group]).map(([key, label]) => (
                <Checkbox
                  key={key}
                  label={label}
                  checked={draft.amenities[group].includes(key)}
                  disabled={locked('amenities')}
                  onChange={(event) =>
                    onChange('amenities', {
                      ...draft.amenities,
                      [group]: toggled(draft.amenities[group], key, event.target.checked),
                    })
                  }
                />
              ))}
            </div>
          </fieldset>
        ))}
      </div>
      <div className={styles.group}>
        <div className={styles.groupTitle}>Energie</div>
        <div className={styles.grid3}>
          <Select
            label="Energieausweis"
            value={draft.energyCertificateType}
            placeholder="Keine Angabe"
            options={ENERGY_CERTIFICATE_TYPES.map((key) => ({
              value: key,
              label: energyCertificateTypeLabels[key],
            }))}
            {...lock('energyCertificateType')}
            onChange={(event) =>
              onChange('energyCertificateType', event.target.value as EnergyCertificateType | '')
            }
          />
          <TextField
            label="Kennwert"
            inputMode="decimal"
            trailing={<span className={styles.suffix}>kWh/(m²·a)</span>}
            value={draft.energyValueKwhSqm}
            error={fields['energyValueKwhSqm']}
            {...lock('energyValueKwhSqm')}
            onChange={(event) => onChange('energyValueKwhSqm', event.target.value)}
          />
          <Select
            label="Energieklasse"
            value={draft.energyClass}
            placeholder="Keine Angabe"
            options={ENERGY_CLASSES.map((key) => ({ value: key, label: key }))}
            {...lock('energyClass')}
            onChange={(event) => onChange('energyClass', event.target.value as EnergyClass | '')}
          />
        </div>
        <Select
          label="KfW-Standard"
          value={draft.kfwStandard}
          placeholder="Keine Angabe"
          options={Object.entries(KFW_STANDARD_LABELS).map(([value, label]) => ({ value, label }))}
          {...lock('kfwStandard')}
          onChange={(event) => onChange('kfwStandard', event.target.value)}
        />
        <fieldset className={styles.checkGroup}>
          <legend className={styles.fieldLabel}>Energieträger</legend>
          {locked('energyCarriers') && external ? (
            <span className={styles.lockNote}>{managedHint(external)}</span>
          ) : null}
          <div className={styles.checkGrid}>
            {Object.entries(ENERGY_CARRIER_LABELS).map(([key, label]) => (
              <Checkbox
                key={key}
                label={label}
                checked={draft.energyCarriers.includes(key)}
                disabled={locked('energyCarriers')}
                onChange={(event) =>
                  onChange('energyCarriers', toggled(draft.energyCarriers, key, event.target.checked))
                }
              />
            ))}
          </div>
        </fieldset>
        <fieldset className={styles.checkGroup}>
          <legend className={styles.fieldLabel}>Heizung</legend>
          {locked('heatingSystems') && external ? (
            <span className={styles.lockNote}>{managedHint(external)}</span>
          ) : null}
          <div className={styles.checkGrid}>
            {Object.entries(HEATING_SYSTEM_LABELS).map(([key, label]) => (
              <Checkbox
                key={key}
                label={label}
                checked={draft.heatingSystems.includes(key)}
                disabled={locked('heatingSystems')}
                onChange={(event) =>
                  onChange('heatingSystems', toggled(draft.heatingSystems, key, event.target.checked))
                }
              />
            ))}
          </div>
        </fieldset>
      </div>
    </>
  );

  const rent = (
    <>
      <div className={styles.group}>
        <div className={styles.groupTitle}>Miete</div>
        <div className={styles.grid2}>
          <Checkbox
            label="Vermietet"
            checked={draft.rented}
            onChange={(event) => onChange('rented', event.target.checked)}
          />
          {draft.rented ? (
            <TextField
              label="Vermietet seit"
              type="date"
              value={draft.rentedSinceOn}
              error={fields['rentedSinceOn']}
              onChange={(event) => onChange('rentedSinceOn', event.target.value)}
            />
          ) : (
            <span />
          )}
          {moneyField('rentActualCents', 'Miete Ist mtl.')}
          {moneyField('rentTargetCents', 'Miete Soll mtl.')}
          {moneyField('parkingSpaceRentCents', 'Stellplatzmiete mtl.')}
        </div>
      </div>
      <div className={styles.group}>
        <div className={styles.groupTitle}>Laufende Kosten</div>
        <div className={styles.grid2}>
          {moneyField('serviceChargeCents', 'Hausgeld mtl.')}
          {moneyField(
            'reserveShareCents',
            'davon Instandhaltungsrücklage mtl.',
            inherited('reserveShareCents'),
          )}
          {moneyField('sevCents', 'SE-Verwaltung mtl.', inherited('sevCents'))}
          {moneyField('wegCents', 'WEG-Verwaltung mtl.', inherited('wegCents'))}
          {moneyField(
            'otherNonRecoverableCents',
            'Sonstige nicht umlagefähige Kosten mtl.',
            inherited('otherNonRecoverableCents'),
          )}
        </div>
        <p className={styles.helper}>
          Hausgeld ist ein Fakt der Einheit; der Rechner verwendet SE-Verwaltung, WEG-Verwaltung, sonstige
          nicht umlagefähige Kosten und die Instandhaltungsrücklage.
        </p>
        {inheritanceLinks}
      </div>
      <div className={styles.group}>
        <div className={styles.groupTitle}>Zuschüsse</div>
        <div className={styles.grid2}>
          {moneyField('rentSubsidyCents', 'Mietsubvention einmalig')}
          <TextField
            label="Subventionszyklen (à 36 Monate)"
            type="number"
            inputMode="numeric"
            min={0}
            step={1}
            value={draft.rentSubsidyCycles}
            error={fields['rentSubsidyCycles']}
            onChange={(event) => onChange('rentSubsidyCycles', event.target.value)}
          />
          {moneyField('renovationSubsidyCents', 'Renovierungszuschuss')}
          {moneyField('maintenanceExpenseCents', 'Erhaltungsaufwand einmalig')}
        </div>
      </div>
    </>
  );

  const calculation = (
    <>
      <div className={styles.group}>
        <div className={styles.groupTitle}>Preis</div>
        <div className={styles.grid2}>
          {moneyField('priceCents', 'Kaufpreis')}
          <TextField
            label="Provisionsfähiger Kaufpreis"
            type="money"
            hint="Ohne Angabe gilt der Kaufpreis."
            value={draft.commissionablePurchasePriceCents}
            error={fields['commissionablePurchasePriceCents']}
            onChange={(event) => onChange('commissionablePurchasePriceCents', event.target.value)}
          />
        </div>
      </div>
      <div className={styles.group}>
        <div className={styles.groupTitle}>Kaufpreisaufteilung</div>
        <div className={styles.grid2}>
          {moneyField('priceLandCents', 'Grund und Boden')}
          {moneyField('priceExistingBuildingCents', 'Altbausubstanz')}
          {moneyField('renovationCents', 'Sanierung')}
          {moneyField('priceNewConstructionCents', 'Neubau')}
          {moneyField('priceFurnitureCents', 'Möblierung')}
          {moneyField('priceOutdoorFacilitiesCents', 'Außenanlagen')}
        </div>
      </div>
      <div className={styles.group}>
        <div className={styles.groupTitle}>Abschreibung und Anteile</div>
        <div className={styles.grid2}>
          {percentField('depreciationPct', 'AfA-Satz', 0.05, 10, inherited('depreciationPct'))}
          {percentField('landSharePct', 'Grundstücksanteil', 0.01, 100)}
          {percentField('oldBuildingSharePct', 'Altbauanteil', 0.01, 100)}
          <TextField
            label="Miteigentumsanteil"
            inputMode="decimal"
            trailing={<span className={styles.suffix}>/ 1.000</span>}
            value={draft.coOwnershipShare}
            error={fields['coOwnershipShare']}
            {...lock('coOwnershipShare')}
            onChange={(event) => onChange('coOwnershipShare', event.target.value)}
          />
        </div>
        {inheritanceLinks}
        <div className={styles.fieldBlock}>
          <Toggle
            label="Verkäufer zahlt Kaufnebenkosten"
            checked={draft.sellerPaysAcquisitionCosts}
            onChange={(checked) => onChange('sellerPaysAcquisitionCosts', checked)}
          />
          <span className={styles.helper}>Kaufpreis wird im Rechner auf die Gesamtkosten hochgerechnet</span>
        </div>
      </div>
    </>
  );

  const block = unit.fields.externalBlock;
  const status = (
    <div className={styles.group}>
      <div className={styles.grid2}>
        <TextField
          label="Status"
          readOnly
          value={unit.statusPill}
          hint="Ergibt sich aus Reservierung und Beurkundung."
        />
        <TextField
          label="Beurkundet am"
          readOnly
          value={unit.fields.notarizedOn ? formatDate(unit.fields.notarizedOn) : '—'}
        />
        <TextField
          label="Externer Block"
          readOnly
          value={block === null || block === 'free' ? 'Kein Block' : unitExternalBlockLabels[block]}
          hint={external ? managedHint(external) : undefined}
        />
      </div>
      <Checkbox
        label="Vertriebsfreigabe"
        description={
          locked('salesReleased') && external
            ? managedHint(external)
            : 'Nur freigegebene Einheiten können reserviert werden.'
        }
        checked={draft.salesReleased}
        disabled={locked('salesReleased')}
        onChange={(event) => onChange('salesReleased', event.target.checked)}
      />
    </div>
  );

  const panels: Record<UnitEditTab, ReactNode> = { location, rent, calculation, status };

  return (
    <div className={styles.form}>
      {hasManagedFields(external) && external ? (
        <Banner tone="info">
          Felder mit Schloss werden aus {external.connectionName} übernommen und können hier nicht geändert
          werden.
        </Banner>
      ) : null}
      <Tabs<UnitEditTab>
        label="Bereiche der Einheit"
        items={items}
        value={tab}
        onChange={onTab}
        panelId="unit-edit"
        className={tabStyles.tabs}
      />
      <div
        id={`unit-edit-${tab}`}
        role="tabpanel"
        aria-label={UNIT_EDIT_TABS.find((item) => item.value === tab)?.label}
        className={tabStyles.panel}
      >
        {panels[tab]}
      </div>
    </div>
  );
}

/**
 * Objekte 04 „Einheit bearbeiten": the unit's fields → `PATCH /units/:id`. The extended data of migration 0029
 * (Untertyp … KfW-Standard, Kaufpreisaufteilung, Ausstattung) has no design screen (D-188). Fields owned by
 * TeamProQ while the connection is import-only are read-only with a lock and are never sent.
 */
export function UnitEditDialog({
  open,
  unit,
  initialTab = 'location',
  onClose,
  onSaved,
}: UnitEditDialogProps) {
  const [draft, setDraft] = useState<UnitDraft>(() => unitDraftOf(unit));
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [tab, setTab] = useState<UnitEditTab>(initialTab);
  const property = usePropertyRecord(unit.propertyId, open);
  const org = useOrganizationHints(open);
  const mayEditOrganization = usePermissions().is('settings.organization', 'yes');
  const external = unit.external;

  useEffect(() => {
    if (open) {
      setDraft(unitDraftOf(unit));
      setFields({});
      setError(null);
      setTab(initialTab);
    }
  }, [open, unit, initialTab]);

  const record = property.data;
  const hints = record ? unitInheritanceHints(record.calcDefaults, org) : {};
  const links: InheritanceLinks = {
    property: record?.canEdit ? `/properties/${unit.propertyId}?calc=1` : null,
    organization: mayEditOrganization ? '/settings/organization/calculation-assumptions' : null,
  };

  function showErrors(errors: Record<string, string>) {
    setFields(errors);
    const counts = errorCountsByTab(errors);
    if (counts[tab] === 0) {
      const first = UNIT_EDIT_TABS.find((item) => counts[item.value] > 0);
      if (first) setTab(first.value);
    }
  }

  async function submit() {
    const { body, errors } = unitPatchOf(draft, (field) => isManagedField(external, field));
    if (Object.keys(errors).length > 0) {
      showErrors(errors);
      return;
    }
    setFields({});
    setError(null);
    setPending(true);
    try {
      onSaved(await updateUnit(unit.id, withoutManagedFields(body, external)));
    } catch (err) {
      if (err instanceof ApiError && Object.keys(err.fields).length > 0) showErrors(err.fields);
      else setError(errorMessage(err));
    } finally {
      setPending(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      eyebrow="Einheit"
      title="Einheit bearbeiten"
      subtitle="Die Angaben werden in Reservierung und Kaufvertrag übernommen."
      width={720}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Abbrechen
          </Button>
          <Button loading={pending} onClick={() => void submit()}>
            Speichern
          </Button>
        </>
      }
    >
      {error ? <Banner tone="danger">{error}</Banner> : null}
      <UnitEditForm
        unit={unit}
        draft={draft}
        fields={fields}
        tab={tab}
        onTab={setTab}
        onChange={(key, value) => setDraft((current) => ({ ...current, [key]: value }))}
        hints={hints}
        links={links}
      />
    </Dialog>
  );
}
